import { useClientConfig } from '@framework/auth/client-config/ClientConfigSlice.tsx';
import { type IFrontHeaderTopLayoutProps } from '@framework/theme/front/FrontHeaderTopLayout.tsx';
import { mainMenuTitleWrapStyle } from '@framework/theme/front/mainMenuTitleWrapStyle.ts';
import { AffixOrder } from '@framework/ui/components/affix/AffixManagerStore.ts';
import { ManagedAffix } from '@framework/ui/components/affix/ManagedAffix.tsx';
import { ContentLayoutWrapper } from '@framework/ui/components/layout/ContentLayoutWrapper.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { Button, Col, Layout, Row } from 'antd';
import clx from 'classnames';
import { useState } from 'react';
import { down, up } from 'styled-breakpoints';
import styled, { useTheme } from 'styled-components';
import { FrontHeaderLogo } from './header/FrontHeaderLogo.tsx';

const { Header } = Layout;

export const StyledHeader = styled(Header)`
  &&& {
    width: 100%;
    padding: 0;
    position: relative;
    z-index: 11;
    height: auto;
    line-height: unset;
    background-color: ${props => props.theme.colorPrimary};
    color: ${props => props.theme.colorWhite};
    &.fixed-header {
      position: fixed;
    }
  }
`;

const RowNoWrap = styled(Row)`
  flex-wrap: nowrap;
  /* height: ${props => props.theme.layoutHeaderContentHeight}px; */
  max-width: 100%;
  padding: 16px 24px;
  ${down('sm')} {
    padding: 16px 12px;
  }
  /* &.boxed {
    padding: 16px 24px;
    ${down('sm')} {
      padding: 16px 12px;
    }
  } */
`;
const ColFixed = styled(Col)`
  flex: 0 0 auto;
  min-width: 0;
  align-items: center;
`;
const HeaderBanner = styled.div`
  padding: 12px 0;
  display: flex;
  > div {
    width: 100%;
  }
  align-items: center;
  ${up('sm')} {
    min-height: 48px;
  }

  background-color: ${props => props.theme.layoutHeaderTopBgColor};
  color: ${props => props.theme.layoutHeaderTopColor};
  a {
    color: ${props => props.theme.layoutHeaderTopColor};
    &:hover {
      text-decoration: underline;
    }
  }
`;
export const HeaderMenuBackgroundColor = styled.div`
  background-color: ${props => props.theme.colorPrimary};
  color: ${props => props.theme.colorWhite};
`;
const ColAuto = styled(Col)`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  justify-content: end;
`;

export const MenuBar = styled.div`
  padding: 0 24px;
  ${down('sm')} {
    padding: 0 12px;
  }
  ${mainMenuTitleWrapStyle}

  // Stili per i link del menu principale
  .main-menu-wrapper-space a {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    line-height: ${props => props.theme.lineHeight};
    border-bottom: 1px solid transparent;
    border-radius: 1px;
    transition: border-bottom-color 0.2s ease-in-out;

    &:hover {
      border-bottom-color: #fff;
    }

    &:focus-visible {
      outline: 2px solid ${props => props.theme.focusOutlineNegativeColor};
      outline-offset: 3px;
    }
    &::after {
      content: '';
      display: block;
      height: 4px;
      background-color: transparent;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      transition: all 0.8s ease-in-out;
    }

    &.active {
      &::after {
        background-color: white;
      }
      &:hover {
        border-bottom-color: transparent;
      }
    }
  }
`;
const TopLinkWrapper = styled.div`
  padding: 0 24px;
  font-size: 17px;
  font-weight: 600;
  line-height: 17px;
  white-space: nowrap;
  ${down('sm')} {
    padding: 0 12px;
    font-size: 15px;
    line-height: 15px;
  }
`;
const RightLogout = styled.div``;

const WhiteLine = styled.div`
  background-color: #fff;
  opacity: 0.5;
  min-width: 0;
  width: 100%;
  height: 1px;
`;
const MobileAffixedFlexRow = styled(FlexRow)`
  min-height: 54px;
  // altezza dinamica al momento dell'affix con animazione sul padding superiore e inferiore
  padding: 12px 0;
  &.is-mobile-affixed {
    padding: 0;
  }
  transition: padding 0.3s ease-in-out delay 0.1s;
`;

/**
 * Fascia in alto della pagina che contiene il logo,
 * il menu principale, il menu delle notifiche e quello dell'utente.
 */
export function FrontHeaderTopLayout(props: IFrontHeaderTopLayoutProps) {
  const {
    boxed = false,
    main,
    right,
    onLogoClick,
    onLogoutClick,
    isMobile,
  } = props;
  const theme = useTheme();

  const [menuAffixed, setMenuAffixed] = useState<boolean | undefined>(false);
  const hasLogout = !!onLogoutClick;
  const hasMain = main !== undefined || main !== null;

  // Gestione interna della larghezza del'header
  const headerBoxed = theme.boxedFrontofficeHeaderFooterLayoutFlag ?? false;

  const { jsonConfig } = useClientConfig();

  return (
    <StyledHeader>
      {/* Fascia Superiore Header con Link */}
      <HeaderBanner>
        <ContentLayoutWrapper boxed={headerBoxed}>
          <TopLinkWrapper>
            <FlexRow
              right={
                hasLogout && (
                  <RightLogout onClick={onLogoutClick}>
                    <Button type="link">ESCI</Button>
                  </RightLogout>
                )
              }
            >
              <a
                href={
                  jsonConfig?.urlEnte ??
                  'https://www.regione.emilia-romagna.it/'
                }
                target="_blank"
                rel="noreferrer noopener"
              >
                {jsonConfig?.denominazioneEnte ?? 'Regione Emilia-Romagna'}
              </a>
            </FlexRow>
          </TopLinkWrapper>
        </ContentLayoutWrapper>
      </HeaderBanner>

      {/* Fascia con Logo e menu sulla destra */}
      {!isMobile && (
        <ContentLayoutWrapper boxed={headerBoxed}>
          <RowNoWrap align="middle">
            <FrontHeaderLogo onClick={onLogoClick} />
            <ColAuto>
              <div />
            </ColAuto>
            <ColFixed>{right}</ColFixed>
          </RowNoWrap>
        </ContentLayoutWrapper>
      )}

      {/* Fascia con il menu principale */}
      <div>
        {!menuAffixed && !isMobile && (
          <ContentLayoutWrapper boxed={headerBoxed}>
            <MenuBar>
              <WhiteLine />
            </MenuBar>
          </ContentLayoutWrapper>
        )}
        <ManagedAffix
          order={AffixOrder.TOP_BAR}
          asGlobalOffset
          onChange={affixed => setMenuAffixed(affixed)}
          style={{ zIndex: 9 }}
        >
          <HeaderMenuBackgroundColor>
            <ContentLayoutWrapper boxed={headerBoxed}>
              <MenuBar>
                {/* se presente e non è mobile, mostriamo la barra completa del menu (main) */}
                {hasMain && !isMobile && <FlexRow>{main}</FlexRow>}
                {/* se è mobile, ed è affixed spostiamo ricreiamo la Fascia del logo (vedi sopra)
                che include anche il menu di navigazione (right*/}
                {isMobile && (
                  <MobileAffixedFlexRow
                    className={clx({ 'is-mobile-affixed': menuAffixed })}
                    left={<FrontHeaderLogo mobileAffixed />}
                  >
                    {right}
                  </MobileAffixedFlexRow>
                )}
              </MenuBar>
            </ContentLayoutWrapper>
          </HeaderMenuBackgroundColor>
        </ManagedAffix>
      </div>
    </StyledHeader>
  );
}
